package com.videoanalyzer.web.auth

import com.videoanalyzer.web.config.Config
import com.videoanalyzer.web.util.Logger
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.dom.url.URLSearchParams

/**
 * Supabase client for frontend authentication.
 *
 * This handles:
 * - Redirecting to Supabase Auth UI
 * - Parsing auth tokens from URL after redirect
 * - Storing/retrieving tokens
 *
 * ANDROID EQUIVALENT:
 * Like using Firebase Auth's signInWithEmailAndPassword(),
 * but we redirect to Supabase's hosted UI instead.
 *
 * WHY REDIRECT FLOW?
 * - Simpler than managing auth state ourselves
 * - Supabase handles email verification, password reset
 * - Secure - tokens are handled by Supabase
 */
object SupabaseClient {

    // Supabase configuration from Config
    private val SUPABASE_URL = Config.SUPABASE_URL
    private val SUPABASE_ANON_KEY = Config.SUPABASE_ANON_KEY

    // Storage keys
    private const val ACCESS_TOKEN_KEY = "supabase_access_token"
    private const val REFRESH_TOKEN_KEY = "supabase_refresh_token"
    private const val USER_ID_KEY = "supabase_user_id"
    private const val USER_EMAIL_KEY = "supabase_user_email"

    // ============================================================
    // Auth Actions
    // ============================================================

    /**
     * Redirect to Supabase login page.
     *
     * After login, Supabase redirects back to our app with tokens in the URL.
     */
    fun signInWithEmail() {
        val redirectUrl = window.location.origin + "/auth/callback"
        val authUrl = "$SUPABASE_URL/auth/v1/authorize?" +
                "provider=email&" +
                "redirect_to=$redirectUrl"

        window.location.href = authUrl
    }

    /**
     * Redirect to Supabase signup page.
     */
    fun signUpWithEmail() {
        // Supabase's hosted UI handles both login and signup
        signInWithEmail()
    }

    /**
     * Sign in with Google OAuth.
     *
     * ANDROID EQUIVALENT: Like Google Sign-In with Firebase.
     */
    fun signInWithGoogle() {
        val redirectUrl = window.location.origin
        val authUrl = "$SUPABASE_URL/auth/v1/authorize?" +
                "provider=google&" +
                "redirect_to=$redirectUrl"

        window.location.href = authUrl
    }

    /**
     * Sign in with Facebook OAuth.
     */
    fun signInWithFacebook() {
        val redirectUrl = window.location.origin
        val authUrl = "$SUPABASE_URL/auth/v1/authorize?" +
                "provider=facebook&" +
                "redirect_to=$redirectUrl"

        window.location.href = authUrl
    }

    /**
     * Sign up with email and password.
     * Returns a result with success/error message.
     */
    suspend fun signUpWithEmailPassword(email: String, password: String): EmailAuthResult {
        return try {
            val bodyJson = """{"email":"$email","password":"$password"}"""

            val response = window.fetch(
                "$SUPABASE_URL/auth/v1/signup",
                org.w3c.fetch.RequestInit(
                    method = "POST",
                    headers = jsonObject(
                        "Content-Type" to "application/json",
                        "apikey" to SUPABASE_ANON_KEY
                    ),
                    body = bodyJson
                )
            ).await()

            val responseText = response.text().await()

            if (response.ok) {
                // Parse response to check if email confirmation is required
                if (responseText.contains("confirmation_sent_at") || responseText.contains("email_confirmed_at\":null")) {
                    EmailAuthResult.ConfirmationRequired("Please check your email to confirm your account.")
                } else {
                    // Auto-confirmed, parse tokens
                    val accessToken = extractJsonValue(responseText, "access_token")
                    val userId = extractJsonValue(responseText, "id").ifEmpty {
                        // Try to get from user object
                        val userMatch = """"user"\s*:\s*\{[^}]*"id"\s*:\s*"([^"]+)"""".toRegex().find(responseText)
                        userMatch?.groupValues?.getOrNull(1) ?: ""
                    }

                    if (accessToken.isNotEmpty()) {
                        storeTokens(accessToken, null, userId, email)
                        EmailAuthResult.Success(accessToken, userId, email)
                    } else {
                        EmailAuthResult.ConfirmationRequired("Please check your email to confirm your account.")
                    }
                }
            } else {
                val errorMsg = extractJsonValue(responseText, "msg").ifEmpty {
                    extractJsonValue(responseText, "message").ifEmpty { "Sign up failed" }
                }
                EmailAuthResult.Error(errorMsg)
            }
        } catch (e: Exception) {
            EmailAuthResult.Error("Network error: ${e.message}")
        }
    }

    /**
     * Sign in with email and password.
     */
    suspend fun signInWithEmailPassword(email: String, password: String): EmailAuthResult {
        return try {
            val bodyJson = """{"email":"$email","password":"$password"}"""

            val response = window.fetch(
                "$SUPABASE_URL/auth/v1/token?grant_type=password",
                org.w3c.fetch.RequestInit(
                    method = "POST",
                    headers = jsonObject(
                        "Content-Type" to "application/json",
                        "apikey" to SUPABASE_ANON_KEY
                    ),
                    body = bodyJson
                )
            ).await()

            val responseText = response.text().await()

            if (response.ok) {
                val accessToken = extractJsonValue(responseText, "access_token")
                val refreshToken = extractJsonValue(responseText, "refresh_token")
                val userId = extractJsonValue(responseText, "sub").ifEmpty {
                    // Try to decode from JWT
                    decodeJwtPayload(accessToken)?.userId ?: ""
                }

                if (accessToken.isNotEmpty()) {
                    storeTokens(accessToken, refreshToken, userId, email)
                    EmailAuthResult.Success(accessToken, userId, email)
                } else {
                    EmailAuthResult.Error("Invalid response from server")
                }
            } else {
                val errorMsg = extractJsonValue(responseText, "error_description").ifEmpty {
                    extractJsonValue(responseText, "msg").ifEmpty { "Invalid email or password" }
                }
                EmailAuthResult.Error(errorMsg)
            }
        } catch (e: Exception) {
            EmailAuthResult.Error("Network error: ${e.message}")
        }
    }

    /**
     * Helper to create a JSON object for headers.
     */
    private fun jsonObject(vararg pairs: Pair<String, String>): dynamic {
        val obj = js("{}")
        pairs.forEach { (key, value) ->
            obj[key] = value
        }
        return obj
    }

    /**
     * Sign out - clear all stored tokens.
     */
    fun signOut() {
        try {
            localStorage.removeItem(ACCESS_TOKEN_KEY)
            localStorage.removeItem(REFRESH_TOKEN_KEY)
            localStorage.removeItem(USER_ID_KEY)
            localStorage.removeItem(USER_EMAIL_KEY)
        } catch (e: Exception) {
            Logger.error("Error clearing tokens", e)
        }
    }

    /**
     * Request a password reset email.
     * Supabase will send an email with a reset link.
     */
    suspend fun requestPasswordReset(email: String): PasswordResetResult {
        return try {
            val redirectUrl = window.location.origin + "/#type=recovery"
            val bodyJson = """{"email":"$email","redirect_to":"$redirectUrl"}"""

            val response = window.fetch(
                "$SUPABASE_URL/auth/v1/recover",
                org.w3c.fetch.RequestInit(
                    method = "POST",
                    headers = jsonObject(
                        "Content-Type" to "application/json",
                        "apikey" to SUPABASE_ANON_KEY
                    ),
                    body = bodyJson
                )
            ).await()

            if (response.ok) {
                PasswordResetResult.Success
            } else {
                val responseText = response.text().await()
                val errorMsg = extractJsonValue(responseText, "msg").ifEmpty {
                    extractJsonValue(responseText, "message").ifEmpty { "Failed to send reset email" }
                }
                PasswordResetResult.Error(errorMsg)
            }
        } catch (e: Exception) {
            PasswordResetResult.Error("Network error: ${e.message}")
        }
    }

    /**
     * Update user's password using the recovery token.
     * This is called after user clicks the reset link in their email.
     */
    suspend fun updatePassword(accessToken: String, newPassword: String): PasswordResetResult {
        return try {
            val bodyJson = """{"password":"$newPassword"}"""

            val response = window.fetch(
                "$SUPABASE_URL/auth/v1/user",
                org.w3c.fetch.RequestInit(
                    method = "PUT",
                    headers = jsonObject(
                        "Content-Type" to "application/json",
                        "apikey" to SUPABASE_ANON_KEY,
                        "Authorization" to "Bearer $accessToken"
                    ),
                    body = bodyJson
                )
            ).await()

            if (response.ok) {
                PasswordResetResult.Success
            } else {
                val responseText = response.text().await()
                val errorMsg = extractJsonValue(responseText, "msg").ifEmpty {
                    extractJsonValue(responseText, "message").ifEmpty { "Failed to update password" }
                }
                PasswordResetResult.Error(errorMsg)
            }
        } catch (e: Exception) {
            PasswordResetResult.Error("Network error: ${e.message}")
        }
    }

    /**
     * Parse recovery token from URL hash after clicking reset link.
     * Supabase redirects with: #type=recovery&access_token=xxx
     */
    fun parseRecoveryToken(): String? {
        val hash = window.location.hash
        if (hash.isBlank() || !hash.contains("type=recovery")) {
            return null
        }

        val params = hash.removePrefix("#")
            .split("&")
            .associate {
                val parts = it.split("=", limit = 2)
                parts[0] to (parts.getOrNull(1) ?: "")
            }

        return params["access_token"]
    }

    // ============================================================
    // Token Management
    // ============================================================

    /**
     * Check if user is logged in.
     */
    fun isLoggedIn(): Boolean {
        return getAccessToken() != null
    }

    /**
     * Get stored access token.
     */
    fun getAccessToken(): String? {
        return try {
            localStorage.getItem(ACCESS_TOKEN_KEY)
        } catch (e: Exception) {
            null
        }
    }

    /**
     * Get stored user ID.
     */
    fun getUserId(): String? {
        return try {
            localStorage.getItem(USER_ID_KEY)
        } catch (e: Exception) {
            null
        }
    }

    /**
     * Get stored user email.
     */
    fun getUserEmail(): String? {
        return try {
            localStorage.getItem(USER_EMAIL_KEY)
        } catch (e: Exception) {
            null
        }
    }

    /**
     * Store auth tokens.
     */
    fun storeTokens(accessToken: String, refreshToken: String?, userId: String, email: String) {
        try {
            localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
            refreshToken?.let { localStorage.setItem(REFRESH_TOKEN_KEY, it) }
            localStorage.setItem(USER_ID_KEY, userId)
            localStorage.setItem(USER_EMAIL_KEY, email)
        } catch (e: Exception) {
            Logger.error("Error storing tokens", e)
        }
    }

    // ============================================================
    // URL Parsing (after redirect)
    // ============================================================

    /**
     * Parse auth tokens from URL hash after Supabase redirect.
     *
     * Supabase redirects back with tokens in the URL fragment:
     * https://yoursite.com/#access_token=xxx&refresh_token=yyy&...
     *
     * @return AuthResult with tokens if found, null otherwise
     */
    fun parseAuthFromUrl(): AuthResult? {
        val hash = window.location.hash
        if (hash.isBlank() || !hash.contains("access_token")) {
            return null
        }

        // Parse hash parameters
        // Format: #access_token=xxx&refresh_token=yyy&expires_in=3600&token_type=bearer
        val params = hash.removePrefix("#")
            .split("&")
            .associate {
                val parts = it.split("=", limit = 2)
                parts[0] to (parts.getOrNull(1) ?: "")
            }

        val accessToken = params["access_token"] ?: return null
        val refreshToken = params["refresh_token"]

        // Decode JWT to get user info
        val userInfo = decodeJwtPayload(accessToken)

        // Clear the hash from URL (clean up)
        window.history.replaceState(null, "", window.location.pathname)

        return AuthResult(
            accessToken = accessToken,
            refreshToken = refreshToken,
            userId = userInfo?.userId ?: "",
            email = userInfo?.email ?: ""
        )
    }

    /**
     * Simple JWT payload decoder.
     *
     * JWT format: header.payload.signature
     * We decode the payload (base64) to get user info.
     */
    private fun decodeJwtPayload(token: String): JwtUserInfo? {
        return try {
            val parts = token.split(".")
            if (parts.size != 3) return null

            // Decode base64 payload
            val payload = parts[1]
            // Add padding if needed
            val padded = when (payload.length % 4) {
                2 -> "$payload=="
                3 -> "$payload="
                else -> payload
            }

            val decoded = window.atob(padded)

            // Parse JSON manually (simple approach)
            val userId = extractJsonValue(decoded, "sub")
            val email = extractJsonValue(decoded, "email")

            JwtUserInfo(userId, email)
        } catch (e: Exception) {
            Logger.error("Error decoding JWT", e)
            null
        }
    }

    /**
     * Extract a string value from JSON (simple regex approach).
     */
    private fun extractJsonValue(json: String, key: String): String {
        val regex = """"$key"\s*:\s*"([^"]+)"""".toRegex()
        return regex.find(json)?.groupValues?.getOrNull(1) ?: ""
    }
}

/**
 * Result from parsing auth URL.
 */
data class AuthResult(
    val accessToken: String,
    val refreshToken: String?,
    val userId: String,
    val email: String
)

/**
 * User info extracted from JWT.
 */
private data class JwtUserInfo(
    val userId: String,
    val email: String
)

/**
 * Result from email/password authentication.
 */
sealed class EmailAuthResult {
    data class Success(val accessToken: String, val userId: String, val email: String) : EmailAuthResult()
    data class ConfirmationRequired(val message: String) : EmailAuthResult()
    data class Error(val message: String) : EmailAuthResult()
}

/**
 * Result from password reset request.
 */
sealed class PasswordResetResult {
    object Success : PasswordResetResult()
    data class Error(val message: String) : PasswordResetResult()
}
